/**
 * Single source of truth for VerSports design tokens.
 *
 * Both apps import from here:
 *   - vr-app: `vr-app/src/ui/theme/presets.ts` re-exports these as Theme
 *     instances (with extra world/3D fields layered on top).
 *   - web-app: `web-app/tailwind.config.ts` consumes via `@versports/theme/tailwind`
 *     to populate `theme.extend.colors / fontSize / spacing / radius`.
 *
 * The active palette follows the VerSports brand book:
 * Lab = bone/sand/ink, Stage = amethyst/ember/paper. The apps default to the
 * Stage treatment because most surfaces sit on dark, immersive backgrounds.
 * Don't change a hex here without updating the consuming docs in the same
 * commit, and test both surfaces (web + VR) to make sure nothing reads weird.
 */

export type Hex = `#${string}`

export interface Palette {
  primary: Hex
  primaryFg: Hex
  secondary: Hex
  accent: Hex
  surface: Hex
  surfaceElevated: Hex
  surfaceHighlight: Hex
  outline: Hex
  textPrimary: Hex
  textSecondary: Hex
  textMuted: Hex
  goal: Hex
  warning: Hex
  danger: Hex
}

export const brandColors = {
  bone: '#F1EEE3',
  sand: '#E6E2D4',
  line: '#CFC9B8',
  slate: '#4A463B',
  ink: '#1A1A17',
  amethyst: '#272049',
  amethystSurface: '#322A55',
  amethystLine: '#423A66',
  amethystSoft: '#B7B0CC',
  amethystPaper: '#F4F2F7',
  ember: '#E25A1B',
} as const

/** Currently active palette — brand Stage. */
export const palette: Palette = {
  primary: '#E25A1B',
  primaryFg: '#FFFFFF',
  secondary: '#B7B0CC',
  accent: '#E25A1B',
  surface: '#272049',
  surfaceElevated: '#322A55',
  surfaceHighlight: '#423A66',
  outline: '#423A66',
  textPrimary: '#F4F2F7',
  textSecondary: '#B7B0CC',
  textMuted: '#8D86A8',
  goal: '#6fae3d',
  warning: '#E2A51B',
  danger: '#D84A2B',
}

export const typography = {
  displayFamily:
    '"Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  family:
    '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  monoFamily: '"IBM Plex Mono", ui-monospace, "Menlo", "Cascadia Code", monospace',
  weight: { regular: 400, medium: 500, bold: 700 },
  fontSizePx: { xs: 11, sm: 13, md: 15, lg: 18, xl: 28, xxl: 44 },
  lineHeight: 1.35,
  tracking: { tight: '-0.02em', normal: '0em', wide: '0.04em' },
} as const

export const spacingPx = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 40,
} as const

export const radiusPx = {
  sm: 6,
  md: 10,
  lg: 14,
} as const

export const shadow = {
  elevated: '0 8px 24px rgba(0, 0, 0, 0.35)',
} as const
